import React from "react";
import * as Mui from "@mui/material";
import { ArrowForwardRounded, EventNoteRounded } from "@mui/icons-material";
import { designColors } from "services/constants/designTokens";
import { ctaButtonSx, sectionCardSx, SectionHeader } from "./homeUi";

const donutSize = 176;
const ringThickness = 27;
const donutRadius = donutSize / 2;
const ringMidRadius = donutRadius - ringThickness / 2;

const buildDoughnutSegments = (counts) => {
  const total = counts?.overall_count ?? 0;
  const pct = (count) => (total ? (count / total) * 100 : 0);
  return [
    {
      label: "Present",
      value: counts?.present_count ?? 0,
      color: "#16A34A",
      pct: pct(counts?.present_count ?? 0),
    },
    {
      label: "Absent",
      value: counts?.absent_count ?? 0,
      color: "#EF4444",
      pct: pct(counts?.absent_count ?? 0),
    },
    {
      label: "On Leave",
      value: counts?.leave_count ?? 0,
      color: "#7C3AED",
      pct: pct(counts?.leave_count ?? 0),
    },
    {
      label: "Week Off",
      value: counts?.weekoff_count ?? 0,
      color: "#9CA3AF",
      pct: pct(counts?.weekoff_count ?? 0),
    },
  ];
};

export const WorkforceStatusSection = ({
  navigateToModule,
  attendanceCounts,
  loading = false,
  dateSuffix = "Today",
}) => {
  const totalMembers = attendanceCounts?.overall_count ?? 0;
  const doughnutSegments = buildDoughnutSegments(attendanceCounts);
  // Planned isn't a slice of this donut — it overlaps with Present/Absent/
  // Leave/Week Off rather than being exclusive of them (a planned member
  // can land in any of those buckets), so it surfaces as a header chip
  // instead of a 5th segment. Same hide rule as the WorkforceSummaryCards
  // "Planned" stat card: only worth calling out when it diverges from who
  // actually showed up.
  const showPlannedChip =
    (attendanceCounts?.planned_count ?? 0) !== (attendanceCounts?.present_count ?? 0);

  let cumulativePct = 0;
  const gradientStops = doughnutSegments
    .map((segment) => {
      const start = cumulativePct;
      cumulativePct += segment.pct;
      return `${segment.color} ${start}% ${cumulativePct}%`;
    })
    .join(", ");

  cumulativePct = 0;
  const doughnutSegmentsWithLabelPos = doughnutSegments.map((segment) => {
    const midPct = cumulativePct + segment.pct / 2;
    cumulativePct += segment.pct;
    const angleRad = (midPct / 100) * 2 * Math.PI;
    return {
      ...segment,
      labelX: ringMidRadius * Math.sin(angleRad),
      labelY: -ringMidRadius * Math.cos(angleRad),
    };
  });

  return (
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // Respond to this card's own width, not the window (sidebar and OS scaling are ignored otherwise).
        containerType: "inline-size",
      }}
    >
      {!loading && (
        <SectionHeader
          title={
            dateSuffix === "Today"
              ? "Today's Workforce Status"
              : `Workforce Status (${dateSuffix})`
          }
          tooltip="How many team members are Present, Absent, On Leave, or on a Week Off, plus how many have a shift planned for the selected day."
          endAdornment={
            showPlannedChip ? (
              <Mui.Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() =>
                  navigateToModule("Attendance", "component=employee-view&workforce=planned")
                }
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  backgroundColor: designColors.tealTint,
                  cursor: "pointer",
                  transition: "background-color 120ms ease",
                  "&:hover": {
                    backgroundColor: designColors.teal,
                    "& .MuiTypography-root, & .MuiSvgIcon-root": {
                      color: designColors.white,
                    },
                  },
                }}
              >
                <EventNoteRounded
                  sx={{ fontSize: 15, color: designColors.tealDark }}
                />
                <Mui.Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: designColors.tealDark,
                    whiteSpace: "nowrap",
                  }}
                >
                  {attendanceCounts?.planned_count ?? 0} Planned
                </Mui.Typography>
              </Mui.Stack>
            ) : null
          }
        />
      )}

      {loading ? (
        <>
          <Mui.Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              flex: 1,
              pt: 1.5,
              "@container (max-width: 480px)": {
                flexDirection: "column",
                "& > :not(style) ~ :not(style)": { marginLeft: 0, marginTop: 2 },
              },
            }}
          >
            <Mui.Skeleton
              variant="circular"
              width={donutSize}
              height={donutSize}
              sx={{ flexShrink: 0 }}
            />
            <Mui.Stack spacing={1.15} sx={{ flex: 1, width: "100%" }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Mui.Box key={index}>
                  <Mui.Skeleton variant="text" width="44%" height={26} />
                  <Mui.Skeleton variant="text" width="28%" height={30} />
                </Mui.Box>
              ))}
            </Mui.Stack>
          </Mui.Stack>
          <Mui.Skeleton
            variant="rounded"
            height={36}
            sx={{ borderRadius: 999, mt: 1.5 }}
          />
        </>
      ) : (
        <>
          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, pt: 1.5 }}
          >
            <Mui.Box
              sx={{
                width: donutSize,
                height: donutSize,
                borderRadius: "50%",
                background: `conic-gradient(${gradientStops})`,
                position: "relative",
                flexShrink: 0,
                "@container (max-width: 480px)": { mx: "auto" },
              }}
            >
              <Mui.Box
                sx={{
                  position: "absolute",
                  inset: ringThickness,
                  borderRadius: "50%",
                  backgroundColor: designColors.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  textAlign: "center",
                }}
              >
                <Mui.Typography
                  sx={{ fontSize: 26, fontWeight: 700, color: "#16A34A" }}
                >
                  {totalMembers}
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    fontSize: 13,
                    color: designColors.slate600,
                    lineHeight: 1.2,
                  }}
                >
                  Team
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    fontSize: 13,
                    color: designColors.slate600,
                    lineHeight: 1.2,
                  }}
                >
                  Members
                </Mui.Typography>
              </Mui.Box>
              {doughnutSegmentsWithLabelPos.map((segment) => (
                <Mui.Typography
                  key={`${segment.label}-count`}
                  sx={{
                    position: "absolute",
                    top: `calc(50% + ${segment.labelY}px)`,
                    left: `calc(50% + ${segment.labelX}px)`,
                    transform: "translate(-50%, -50%)",
                    fontSize: 12,
                    fontWeight: 700,
                    color: designColors.white,
                    textShadow: "0 1px 1.5px rgba(0, 0, 0, 0.25)",
                    lineHeight: 1,
                  }}
                >
                  {segment.value}
                </Mui.Typography>
              ))}
            </Mui.Box>

            <Mui.Stack
              spacing={1.1}
              sx={{
                flex: 1,
                minWidth: 0,
                "@container (max-width: 480px)": { width: "100%" },
              }}
            >
              {doughnutSegments.map((segment) => (
                <Mui.Box key={segment.label} sx={{ minWidth: 0 }}>
                  <Mui.Stack direction="row" spacing={0.9} alignItems="center">
                    <Mui.Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: segment.color,
                        flexShrink: 0,
                      }}
                    />
                    <Mui.Typography
                      noWrap
                      sx={{
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: designColors.slate900,
                      }}
                    >
                      {segment.label}
                    </Mui.Typography>
                  </Mui.Stack>
                  <Mui.Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="baseline"
                    sx={{ pl: 2.1 }}
                  >
                    <Mui.Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: segment.color,
                      }}
                    >
                      {segment.value}
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 400,
                        color: designColors.slate600,
                      }}
                    >
                      ({segment.pct.toFixed(2)}%)
                    </Mui.Typography>
                  </Mui.Stack>
                </Mui.Box>
              ))}
            </Mui.Stack>
          </Mui.Stack>

          <Mui.Button
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={{ ...ctaButtonSx, mt: 1.5 }}
            onClick={() => navigateToModule("Attendance")}
          >
            View Attendance Overview
          </Mui.Button>
        </>
      )}
    </Mui.Box>
  );
};
